import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ACCENT } from "../artistStudio/shared";
import { apiMessage, apiStatus } from "./fanHomeApi";
import { showError } from "../common/errorToast";
import { usePlayer } from "./PlayerContext";

// Small pieces used by My Library and the playlist page, styled like the My Library screens in the Claude design: the pale
// green list, the cover squares, the dialogs, and a message that fades away. Also the live checking of the playlist name.

// ---- design colours --------------------------------------------------------------------------------------------------------
export const INK = "#14261c";          // headings and titles
export const MUTED = "#6b706c";        // artist names, counts, helper text
export const PANEL_LINE = "#e0e8e3";   // line between rows
const COVERS = ["#cfd9e8", "#e8d4d6", "#cde3d3", "#e0d9c8", "#d6d3e6", "#ece3cf"];
export const coverColour = (seed) => {
  const text = String(seed ?? "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  return COVERS[hash % COVERS.length];
};

// A cover: the picture when there is one, otherwise a soft colour that stays the same for the same item.
export function CoverArt({ url, seed, className = "", children }) {
  return url
    ? <img src={url} alt="" loading="lazy" className={`object-cover ${className}`} />
    : <span className={`flex items-center justify-center ${className}`} style={{ backgroundColor: coverColour(seed) }} aria-hidden="true">{children}</span>;
}

// ---- playlist name rules (the same as the API: 2-100 characters, letters, numbers, spaces and . , ' & ( ) ! ? _ - : #) ----
export const NAME_MIN = 2;
export const NAME_MAX = 100;
const NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} .,'’&()!?_:#-]*$/u;
const normal = (value) => String(value || "").trim().replace(/\s+/g, " ");

// Returns the message to show under the field, or "" when the name is fine. `taken` is the list of names I already have.
export function playlistNameError(value, taken = []) {
  const name = normal(value);
  if (!name) return "Enter a playlist name.";
  if (name.length < NAME_MIN) return `The name must be at least ${NAME_MIN} characters.`;
  if (name.length > NAME_MAX) return `The name can be at most ${NAME_MAX} characters.`;
  if (!NAME_PATTERN.test(name)) return "Use letters, numbers, spaces and . , ' & ( ) ! ? _ - : # in the name.";
  if (taken.some((n) => normal(n).toLowerCase() === name.toLowerCase())) return "You already have a playlist with this name.";
  return "";
}

// ---- a short confirmation message ----------------------------------------------------------------------------------------
export function useToast() {
  const [message, setMessage] = useState("");
  const timer = useRef(null);
  const show = useCallback((text) => {
    setMessage(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(""), 3500);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const toast = (
    <div aria-live="polite" role="status" className="pointer-events-none fixed inset-x-0 bottom-6 z-50 flex justify-center px-4">
      {message && <div className="pointer-events-auto rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-lg" data-testid="toast">{message}</div>}
    </div>
  );
  return [toast, show];
}

// ---- the pale green list (saved sounds, playlist sounds, licences) -------------------------------------------------------
export function ListPanel({ children, className = "", ...props }) {
  return (
    <div className={`overflow-hidden rounded-2xl border bg-[#f1f6f3] ${className}`} style={{ borderColor: PANEL_LINE }} {...props}>{children}</div>
  );
}
export const rowLine = { borderColor: PANEL_LINE };

export function ListSkeleton({ rows = 4 }) {
  return (
    <ListPanel role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex animate-pulse items-center gap-4 border-t px-[18px] py-3 first:border-t-0" style={rowLine}>
          <div className="h-10 w-10 rounded-lg bg-gray-200" />
          <div className="flex-1 space-y-2"><div className="h-3 w-1/3 rounded bg-gray-200" /><div className="h-3 w-1/4 rounded bg-gray-200" /></div>
        </div>
      ))}
    </ListPanel>
  );
}

const PlayTriangle = () => <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" fill="currentColor" aria-hidden="true"><path d="M2 1l7 4-7 4z" /></svg>;

// One sound in a list: play square, title, artist, and an action on the right (Unsave or Remove).
export function SoundRow({ track, queue, action, detail = null }) {
  const { play, isCurrent, isUnavailable, playing } = usePlayer();
  const unavailable = isUnavailable(track.id) || !track.audio_url;
  const active = isCurrent(track.id) && playing;
  return (
    <li className="flex items-center gap-4 border-t px-[18px] py-3 first:border-t-0" style={rowLine}>
      <button
        type="button" onClick={() => !unavailable && play(track, { queue })} disabled={unavailable}
        aria-label={unavailable ? `${track.title} is unavailable` : active ? `Pause ${track.title}` : `Play ${track.title}`}
        className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg disabled:cursor-not-allowed"
      >
        <CoverArt url={track.cover_url} seed={track.id} className={`h-full w-full text-white/90 ${unavailable ? "opacity-40 grayscale" : ""}`}>
          {!unavailable && <PlayTriangle />}
        </CoverArt>
      </button>
      <div className="min-w-0 flex-1 leading-tight">
        <Link to={`/sound/${track.id}`} className="block truncate text-sm font-semibold hover:underline" style={{ color: INK }} title={track.title}>{track.title}</Link>
        <p className="mt-0.5 truncate text-xs" style={{ color: MUTED }}>
          {detail || (track.artist_slug ? <Link to={`/artists/${encodeURIComponent(track.artist_slug)}`} className="hover:underline">{track.artist_name}</Link> : track.artist_name)}
        </p>
      </div>
      {action}
    </li>
  );
}

// ---- buttons ---------------------------------------------------------------------------------------------------------------
const buttonBase = "rounded-full px-[18px] py-2 text-[13px] font-semibold disabled:opacity-60";
export const SolidButton = ({ children, danger = false, className = "", ...props }) => (
  <button type="button" {...props} className={`${buttonBase} text-white ${className}`} style={{ backgroundColor: danger ? "#d9291f" : ACCENT }}>{children}</button>
);
export const OutlineButton = ({ children, danger = false, className = "", ...props }) => (
  <button
    type="button" {...props} className={`${buttonBase} border bg-white ${className}`}
    style={danger ? { borderColor: "#d9291f", color: "#d9291f" } : { borderColor: "#d9dedb", color: INK }}
  >{children}</button>
);

// ---- dialog ----------------------------------------------------------------------------------------------------------------
export function Dialog({ title, onClose, children, busy = false }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, busy]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label={title} className="w-full max-w-[444px] rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-lg font-bold leading-snug" style={{ color: INK }}>{title}</h2>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="-mr-1 -mt-1 rounded-full p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-60">
            <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" /></svg>
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

// Asks before something is deleted. `cancelLabel` is the safe choice on the left, `confirmLabel` the red one on the right.
export function ConfirmDialog({ title, message, confirmLabel, cancelLabel = "Cancel", onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } catch (e) {
      showError(apiMessage(e, "Something went wrong. Please try again."));
      setBusy(false);
    }
  };
  return (
    <Dialog title={title} onClose={onClose} busy={busy}>
      <p className="text-sm leading-relaxed" style={{ color: MUTED }}>{message}</p>
      <div className="mt-6 flex justify-end gap-3">
        <OutlineButton onClick={onClose} disabled={busy}>{cancelLabel}</OutlineButton>
        <SolidButton danger onClick={run} disabled={busy}>{busy ? "Please wait…" : confirmLabel}</SolidButton>
      </div>
    </Dialog>
  );
}

// Create a playlist, or rename one (pass `initial`). The name is checked while typing; the answer from the server is shown
// under the field too (for example when the same name was taken a moment ago on another screen).
export function PlaylistNameDialog({ title, confirmLabel, initial = "", taken = [], onSubmit, onClose }) {
  const [value, setValue] = useState(initial);
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);

  const localError = playlistNameError(value, taken);
  const shown = serverError || (touched || value ? localError : "");
  const unchanged = normal(value) === normal(initial);

  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (localError || busy || unchanged) return;
    setBusy(true);
    setServerError("");
    try {
      await onSubmit(normal(value));
    } catch (e) {
      setServerError(apiStatus(e) === 401 ? "Please log in again." : apiMessage(e, "We couldn't save this. Please try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog title={title} onClose={onClose} busy={busy}>
      <form onSubmit={submit} noValidate>
        <label htmlFor="playlist-name" className="sr-only">Playlist name</label>
        <input
          id="playlist-name" ref={inputRef} type="text" value={value} autoComplete="off" placeholder="Playlist name" maxLength={NAME_MAX + 20}
          onChange={(e) => { setValue(e.target.value); setServerError(""); }}
          onBlur={() => setTouched(true)}
          aria-invalid={Boolean(shown)} aria-describedby="playlist-name-help"
          className={`w-full rounded-lg border px-4 py-2.5 text-sm outline-none focus:ring-2 ${shown ? "border-red-400" : "border-gray-200"}`}
          style={{ color: INK, "--tw-ring-color": ACCENT }}
        />
        <div id="playlist-name-help" className="mt-1 flex min-h-[1rem] justify-between gap-3 text-xs">
          <span className="text-red-600" role={shown ? "alert" : undefined}>{shown}</span>
          {value && <span className={normal(value).length > NAME_MAX ? "text-red-600" : "text-gray-400"}>{normal(value).length}/{NAME_MAX}</span>}
        </div>
        <button
          type="submit" disabled={busy || Boolean(localError) || unchanged}
          className="mt-4 w-full rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ backgroundColor: ACCENT }}
        >{busy ? "Saving…" : confirmLabel}</button>
      </form>
    </Dialog>
  );
}
